"use client";

import React, { useEffect, useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import FaqAnswerView from "@/components/faq/FaqAnswer";
import {
  findFaqCategoryIndexForEntryId,
  getFaqCategories,
  getFaqEntriesForCategory,
  getFaqEntryById,
} from "@/lib/faq";

function readHashEntryId(): string | null {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash.replace(/^#/, "").trim();
  return raw || null;
}

export default function FAQ() {
  const categories = getFaqCategories();
  const [expandedCategories, setExpandedCategories] = useState<number[]>([0]);
  const [focusedEntryId, setFocusedEntryId] = useState<string | null>(null);

  const toggleCategory = (categoryIndex: number) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryIndex)
        ? prev.filter((index) => index !== categoryIndex)
        : [...prev, categoryIndex],
    );
  };

  useEffect(() => {
    const applyHash = () => {
      const entryId = readHashEntryId();
      if (!entryId || !getFaqEntryById(entryId)) {
        setFocusedEntryId(null);
        return;
      }
      const categoryIndex = findFaqCategoryIndexForEntryId(entryId);
      if (categoryIndex < 0) return;

      setFocusedEntryId(entryId);
      setExpandedCategories((prev) =>
        prev.includes(categoryIndex) ? prev : [...prev, categoryIndex],
      );
    };

    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);

  useEffect(() => {
    if (!focusedEntryId) return;
    const categoryIndex = findFaqCategoryIndexForEntryId(focusedEntryId);
    if (categoryIndex < 0 || !expandedCategories.includes(categoryIndex)) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      document
        .getElementById(focusedEntryId)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [focusedEntryId, expandedCategories]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="mb-4 text-4xl font-bold text-foreground">
        Frequently Asked Questions
      </h1>
      <p className="mb-8 text-text-light">
        Find answers to common questions about using Sell It Off
      </p>

      <div className="space-y-6">
        {categories.map((category, categoryIndex) => {
          const entries = getFaqEntriesForCategory(category);
          return (
            <div
              key={category.id}
              className="overflow-hidden rounded-lg border"
            >
              <button
                type="button"
                onClick={() => toggleCategory(categoryIndex)}
                className="flex w-full items-center justify-between bg-secondary p-4 text-left transition-colors hover:bg-tertiary"
              >
                <h2 className="text-xl font-semibold text-foreground">
                  {category.title}
                </h2>
                {expandedCategories.includes(categoryIndex) ? (
                  <ChevronUpIcon className="h-6 w-6 text-text-light" />
                ) : (
                  <ChevronDownIcon className="h-6 w-6 text-text-light" />
                )}
              </button>

              {expandedCategories.includes(categoryIndex) && (
                <div className="space-y-6 px-4 py-6">
                  {entries.map((faq) => {
                    // Anchor id only on first category occurrence (entries may repeat).
                    const isAnchorOwner =
                      findFaqCategoryIndexForEntryId(faq.id) === categoryIndex;
                    return (
                      <div
                        key={`${category.id}-${faq.id}`}
                        id={isAnchorOwner ? faq.id : undefined}
                        className={`scroll-mt-24 border-b border-border pb-6 last:border-b-0 last:pb-0 ${
                          focusedEntryId === faq.id && isAnchorOwner
                            ? "rounded-md bg-primary/5 ring-1 ring-primary/30"
                            : ""
                        }`}
                      >
                        <h3 className="mb-3 text-lg font-semibold text-foreground">
                          {faq.question}
                        </h3>
                        <div className="text-text-light">
                          <FaqAnswerView answer={faq.answer} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-12 rounded-lg bg-secondary p-6">
        <h2 className="mb-4 text-xl font-semibold text-foreground">
          Still have questions?
        </h2>
        <p className="mb-4 text-text-light">
          Can&apos;t find the answer you&apos;re looking for? Our customer
          support team is here to help.
        </p>
        <div className="flex flex-col gap-4 sm:flex-row">
          <a
            href="https://wa.me/2348125554459"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block rounded-md bg-[#25D366] px-4 py-2 text-white hover:bg-[#128C7E] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Contact on WhatsApp
          </a>
          <a
            href="mailto:support@sellitoffnow.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block rounded-md bg-[#16a34a] px-4 py-2 text-white hover:bg-[#166534] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          >
            Email Support
          </a>
        </div>
      </div>
    </div>
  );
}
